'use client';

/*
Comment réserver — mise en page alignée sur la homepage v5.
*/
import Image from 'next/image';
import Markdown from 'markdown-to-jsx';
import Head from '@/core/head';
import { lang, hocT } from '@/helpers/translate';
import { Search } from '@/core/dynamic';
import { useZusModalStore } from '@/helpers/modal.js';
import ModalWrapper from '@/helpers/modals/skin4.js';
import { getCanonicalUrl } from '@/helpers/router.js';
import { openVoiceflowChat } from '@/components/core/voiceflowChat';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import {
  SECTION, CARD, BTN_PRIMARY, BTN_SECONDARY, H2, Kicker, ArrowIcon, NumberedList, FinalBand, kicker,
} from '@/core/editorial';

export default hocT(({ t }) => {
  const { setModal1 } = useZusModalStore.getState();
  const langCurrent = lang();
  const canurls = getCanonicalUrl('howto-ride', langCurrent);

  function openSearch() {
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1} light>
        <div className="fixed left-0 w-full top-10">
          <Search isPopin />
        </div>
      </ModalWrapper>,
    );
  }

  const steps = [0, 1, 2].map((i) => ({ title: t(`berider:steps.${i}.title`), body: t(`berider:steps.${i}.text`) }));
  const smart = [2, 3, 4].map((i) => ({ title: t(`berider:smart.texts.title_${i}`), body: t(`berider:smart.texts.title_${i}_1`) }));
  const keysteps = [1, 2, 3, 4, 5, 6].map((i) => ({ title: t(`berider:keystep.texts.title_${i}`), body: t(`berider:keystep.texts.text_${i}`) }));

  return (
    <div className="bg-white font-display text-was-ink">
      <Head>
        {canurls.map((cprops) => <link key={JSON.stringify(cprops)} {...cprops} />)}
      </Head>

      {/* Hero */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-10 px-6 pb-16 pt-28 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8 lg:pb-24 lg:pt-36">
          <FadeUp>
            <Kicker n="01">{kicker(langCurrent, 'berider', 0)}</Kicker>
            <h1 className="mt-5 max-w-[640px] text-balance font-heading text-[32px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] md:text-[40px] lg:text-[46px]">{t('berider:title')}</h1>
            <p className="mt-6 max-w-[520px] text-lg leading-relaxed text-was-slate">{t('berider:subtitle')}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" className={BTN_PRIMARY} onClick={openSearch}>
                {t('header:begin_search')}
                <ArrowIcon />
              </button>
              <button type="button" className={BTN_SECONDARY} onClick={openVoiceflowChat}>{t('berider:notime_link')}</button>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className={`relative aspect-[4/5] overflow-hidden ${CARD}`}>
            <Image src="/img/berider.jpg" alt={t('berider:title')} fill sizes="(max-width: 1024px) 100vw, 520px" className="object-cover" />
          </FadeUp>
        </div>
      </section>

      {/* Trois étapes */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="02">{kicker(langCurrent, 'berider', 1)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('berider:title_1')}</h2>
          </FadeUp>
          <div className="mt-12 lg:mt-16">
            <NumberedList items={steps} columns={3} />
          </div>
        </div>
      </section>

      {/* Recherche plus intelligente */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <FadeUp>
              <Kicker n="03">{kicker(langCurrent, 'berider', 2)}</Kicker>
              <h2 className={`mt-5 text-balance ${H2} [&_p]:m-0`}><Markdown>{t('berider:smart.title')}</Markdown></h2>
              <div className="mt-5 text-base leading-relaxed text-was-slate [&_strong]:font-medium [&_strong]:text-was-ink"><Markdown>{t('berider:smart.texts.title_1')}</Markdown></div>
            </FadeUp>
            <Stagger stagger={0.1} delay={0.1} className="grid gap-4 sm:grid-cols-2">
              {smart.map((item, i) => (
                <StaggerChild key={item.title} className={`flex flex-col justify-end p-7 ${CARD} ${i === 0 ? 'sm:col-span-2 sm:min-h-[220px]' : ''}`}>
                  <span className="text-sm tabular-nums text-was-muted">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="m-0 mt-3 text-[18px] font-bold leading-tight tracking-[-0.02em]">{item.title}</h3>
                  <p className="m-0 mt-2 text-[15px] leading-relaxed text-was-slate">{item.body}</p>
                </StaggerChild>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* Étapes clés */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="04">{kicker(langCurrent, 'berider', 3)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2} [&_p]:m-0`}><Markdown>{t('berider:keystep.title')}</Markdown></h2>
          </FadeUp>
          <div className="mt-12 lg:mt-16">
            <NumberedList items={keysteps} columns={2} />
          </div>
        </div>
      </section>

      {/* Final */}
      <FinalBand title={t('berider:book.title')}>
        <p className="w-full max-w-[560px] text-base text-white/75">{t('berider:book.text')}</p>
        <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 text-base font-normal text-was-ink transition duration-200 hover:bg-was-violet hover:text-white" onClick={openSearch}>
          {t('berider:btn.search')}
          <ArrowIcon />
        </button>
        <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-7 py-3.5 text-base font-normal text-white transition duration-200 hover:border-white hover:bg-white/10" onClick={openVoiceflowChat}>
          {t('berider:btn.brief')}
        </button>
      </FinalBand>
    </div>
  );
});
